import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Rocket, ArrowUpRight, Mail, MessageCircle, Instagram, Facebook, Linkedin, Youtube, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const EXPLORE_LINKS = [
  { to: "/vision", label: "Vision" },
  { to: "/registrations", label: "Registrations" },
  { to: "/blog", label: "Blog" },
  { to: "/news", label: "News" },
  { to: "/careers", label: "Careers" },
  { to: "/events", label: "Events" },
  { to: "/pricing", label: "Pricing" },
  { to: "/faq", label: "FAQ" },
  { to: "/team", label: "Team" },
  { to: "/portal", label: "Founder Portal" },
  { to: "/contact", label: "Contact" },
];

export function Contact({ settings }) {
  const phone = settings?.phone || "";
  const phone2 = settings?.phone2 || "";
  const address = settings?.address || "";
  const email = settings?.email || "";
  const telHref = phone ? `tel:${phone.replace(/[^+\d]/g, "")}` : "/contact";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 sm:py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,136,0,0.07),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-5">Contact</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gradient-hero" data-testid="contact-headline">
            LET'S BUILD <span className="text-gradient-saffron">WHAT'S NEXT.</span>
          </h2>

          <div className="mt-12 inline-flex flex-col items-center gap-6 glass rounded-2xl px-8 sm:px-14 py-10" data-testid="contact-card">
            <p className="font-display font-extrabold text-2xl tracking-[0.15em] text-white">
              LAUNCH<span className="text-[#FF8800]">LAB</span>
            </p>
            {address && (
              <div className="flex items-start gap-3 text-slate-300 max-w-md" data-testid="contact-address">
                <MapPin className="w-5 h-5 text-[#FF8800] shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base leading-relaxed text-left">{address}</span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-8" data-testid="contact-phones">
              {phone && (
                <a href={telHref} data-testid="contact-phone-link" className="flex items-center gap-2.5 text-slate-200 hover:text-[#FF8800] transition-colors">
                  <Phone className="w-4 h-4 text-[#00E676]" />
                  <span className="font-mono-tech tracking-wider">{phone}</span>
                </a>
              )}
              {phone2 && (
                <a href={`tel:${phone2.replace(/[^+\d]/g, "")}`} data-testid="contact-phone2-link" className="flex items-center gap-2.5 text-slate-400 hover:text-[#FF8800] transition-colors">
                  <Phone className="w-4 h-4 text-[#00B0FF]" />
                  <span className="font-mono-tech tracking-wider">{phone2}</span>
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} data-testid="contact-email-link" className="flex items-center gap-2.5 text-slate-200 hover:text-[#FF8800] transition-colors">
                  <Mail className="w-4 h-4 text-[#00B0FF]" />
                  <span className="font-mono-tech tracking-wider text-sm">{email}</span>
                </a>
              )}
            </div>
            <a
              href={telHref}
              data-testid="contact-cta-button"
              className="group mt-2 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm tracking-wide transition-transform duration-300 hover:scale-105 shadow-[0_0_45px_rgba(255,136,0,0.35)]"
            >
              START A CONVERSATION
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function Footer({ settings: propSettings }) {
  const [fetched, setFetched] = useState(null);
  useEffect(() => {
    if (propSettings === undefined) {
      api.get("/settings").then((r) => setFetched(r.data)).catch(() => setFetched({}));
    }
  }, [propSettings]);
  const settings = propSettings !== undefined ? propSettings : fetched;
  const s = settings || {};

  const socials = [
    { key: "instagram", Icon: Instagram, label: "Instagram" },
    { key: "facebook", Icon: Facebook, label: "Facebook" },
    { key: "linkedin", Icon: Linkedin, label: "LinkedIn" },
    { key: "youtube", Icon: Youtube, label: "YouTube" },
    { key: "x", Icon: Twitter, label: "X" },
  ].filter((soc) => s[soc.key]);

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#04060B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center">
                <Rocket className="w-4 h-4 text-[#06080E]" strokeWidth={2.4} />
              </div>
              <span className="font-display font-extrabold tracking-[0.18em] text-white">
                LAUNCH<span className="text-[#FF8800]">LAB</span>
              </span>
            </div>
            <p className="mt-3 font-mono-tech text-[11px] tracking-[0.3em] text-slate-500 uppercase">
              {s.tagline || "Build. Launch. Transform."}
            </p>
            {s.footer_description && (
              <p className="mt-3 text-xs text-slate-500 leading-relaxed max-w-xs">{s.footer_description}</p>
            )}
            {socials.length > 0 && (
              <div className="mt-4 flex gap-2.5" data-testid="footer-social">
                {socials.map(({ key, Icon, label }) => (
                  <a
                    key={key}
                    href={s[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${key}`}
                    className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-400 hover:text-[#FF8800] transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-4">Explore</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5" data-testid="footer-explore">
              {EXPLORE_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase()}`}
                  className="text-sm text-slate-400 hover:text-[#FF8800] transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="text-sm text-slate-400 space-y-2.5" data-testid="footer-contact">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-slate-500 uppercase mb-4">Contact</p>
            {s.address && <p data-testid="footer-address" className="leading-relaxed">{s.address}</p>}
            {s.phone && (
              <p>
                <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} data-testid="footer-phone-link" className="font-mono-tech text-slate-300 hover:text-[#FF8800] transition-colors">
                  {s.phone}
                </a>
              </p>
            )}
            {s.email && (
              <p>
                <a href={`mailto:${s.email}`} data-testid="footer-email-link" className="font-mono-tech text-slate-300 hover:text-[#FF8800] transition-colors">
                  {s.email}
                </a>
              </p>
            )}
            {s.whatsapp && (
              <p>
                <a
                  href={`https://wa.me/${s.whatsapp.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="inline-flex items-center gap-2 text-slate-300 hover:text-[#00E676] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </p>
            )}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-600" data-testid="footer-copyright">
            {s.footer_copyright || "© 2026 LaunchLab. All Rights Reserved."}
          </p>
          <a href="/admin" data-testid="footer-admin-link" className="text-[11px] font-mono-tech tracking-widest text-slate-700 hover:text-slate-400 transition-colors uppercase">
            Admin
          </a>
        </div>
      </div>
    </footer>
  );
}
